import type { UseFormReturn } from 'react-hook-form'
import { Button } from '../../../../components'
import { PageSkeleton } from '../../../../components/Skeleton'
import { useHrmsAttendanceSettings } from '../hooks/useHrmsAttendanceSettings'
import type { AttendanceSettingsFormValues, AttendanceSourceInfo } from '../types/hrmsAttendanceSettings.types'
import { AttendanceRulesCard } from './AttendanceRulesCard'
import { AttendanceSourceCard } from './AttendanceSourceCard'
import { ConfigurationFooter } from './ConfigurationFooter'
import { WorkingHoursCard } from './WorkingHoursCard'

export interface AttendanceSettingsFormProps {
  source?: AttendanceSourceInfo
  form?: UseFormReturn<AttendanceSettingsFormValues>
  editRules?: boolean
  toggleEditRules?: () => void
  editHours?: boolean
  toggleEditHours?: () => void
  editRegularization?: boolean
  toggleEditRegularization?: () => void
  onDownloadCsvTemplate?: () => void
  onReset?: () => void
  onSubmit?: () => void
  isDirty?: boolean
}

export function AttendanceSettingsForm(props?: AttendanceSettingsFormProps) {
  const {
    isLoading,
    isError,
    error,
    refetch,
    isReadOnly,
    form,
    isDirty,
    isValid,
    isSubmitting,
    editRules,
    toggleEditRules,
    editHours,
    toggleEditHours,
    handleReset,
    handleDownloadTemplate,
    handleSubmit,
    biometricStatus,
    data,
  } = useHrmsAttendanceSettings()

  if (isLoading && !data) {
    return <PageSkeleton />
  }

  if (isError) {
    return (
      <div className="w-full p-6 font-sans">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800 space-y-3">
          <h3 className="text-base font-bold">Failed to load attendance settings</h3>
          <p className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'An error occurred while fetching settings.'}
          </p>
          <Button variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  const biometricEnabled = form.watch('biometricEnabled')
  const manualExcelEnabled = form.watch('manualExcelEnabled')

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5 min-w-0"
    >
      {/* TOP ROW: Attendance Source & Rules cards side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <AttendanceSourceCard
          source={props?.source}
          biometricStatus={biometricStatus}
          biometricEnabled={biometricEnabled}
          manualExcelEnabled={manualExcelEnabled}
          onToggleBiometric={(val) =>
            form.setValue('biometricEnabled', val, { shouldDirty: true, shouldValidate: true })
          }
          onToggleManualExcel={(val) =>
            form.setValue('manualExcelEnabled', val, { shouldDirty: true, shouldValidate: true })
          }
          onDownloadTemplate={props?.onDownloadCsvTemplate || handleDownloadTemplate}
          disabled={isReadOnly}
        />

        <AttendanceRulesCard
          form={form}
          isEditing={editRules}
          onToggleEdit={toggleEditRules}
          isReadOnly={isReadOnly}
        />
      </div>

      {/* WORKING HOURS card: full width */}
      <WorkingHoursCard
        form={form}
        isEditing={editHours}
        onToggleEdit={toggleEditHours}
        isReadOnly={isReadOnly}
      />

      {/* FOOTER card: full width */}
      <ConfigurationFooter
        isDirty={isDirty}
        isValid={isValid}
        onReset={handleReset}
        onSave={handleSubmit}
        isSubmitting={isSubmitting}
        isReadOnly={isReadOnly}
        lastUpdatedAt={
          data?.updatedAt && !isNaN(new Date(data.updatedAt).getTime())
            ? new Date(data.updatedAt).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : undefined
        }
      />
    </form>
  )
}
